import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap";
import { FieldArray, Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import * as _ from "lodash";

import profile from "../../assets/images/profile.png";
import {
  ActivityButton,
  AppRichEditor,
  TimeInput,
} from "../../components/Common";
import {
  State,
  CreateQuestionRequest,
  QuestionDto,
  SkillDto,
  SkillType,
  QuestionImageUploadResponse,
} from "../../interfaces";
import { QuestionModelEnum, QuestionOptionEnum } from "../../enums";
import {
  addQuestion,
  getQuestion,
  updateQuestion,
  uploadQuestionImage,
} from "../../store/actions";
import { toast } from "react-toastify";
import { IMAGE_ACCEPT } from "../../constants";
import { SkillsSelect } from "../../components";
import { generateUniqueId } from "../../services";
import { QuestionContentDto } from "../../interfaces/question/question-content-dto.interface";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
  subjectId: string;
  model: QuestionModelEnum;
};
export const AddEditQuestionsModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
  subjectId,
  model,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  const formRef: any = useRef();

  //#region internal state
  const [request] = useState<CreateQuestionRequest>();
  const [skillsSuggestions, setSkillsSuggestions] = useState<SkillDto[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<any[]>();
  const [imagesProgress, setImagesProgress] = useState<
    {
      isLoading: boolean;
      isSuccess: boolean;
      isError: boolean;
      error: any;
      response: QuestionImageUploadResponse | null;
      tempId?: string;
    }[]
  >([]);
  //#endregion

  //#region redux state
  const {
    loading: addingQuestion,
    success: addQuestionSuccess,
    error: addQuestionError,
  } = useSelector(
    (state: any): State<QuestionDto> => state.question.addQuestion
  );

  const {
    loading: updatingQuestion,
    success: updateQuestionSuccess,
    error: updateQuestionError,
  } = useSelector(
    (state: any): State<QuestionDto> => state.question.updateQuestion
  );

  const {
    error: getQuestionError,
    response: question,
    loading: gettingQuestion,
    success: getQuestionSuccess,
  } = useSelector((state: any): State<QuestionDto> => state.question.question);

  const {
    error: questionImageError,
    loading: questionImageLoading,
    success: questionImageSuccess,
    response: questionImageResponse,
  } = useSelector(
    (state: any): State<QuestionImageUploadResponse> =>
      state.question.questionImage
  );

  const {
    loading: gettingAISKillsSuggestion,
    success: getAISkillsSuggestionSuccess,
    response: aiSuggestedSkills,
  } = useSelector(
    (state: any): State<SkillDto[]> => state.skill.skillsSuggestion
  );

  const { response: skillsBySubject } = useSelector(
    (state: any): State<SkillDto[]> => state.skill.skillsBySubject
  );
  //#endregion

  const validationSchema = Yup.object<CreateQuestionRequest>().shape({
    contents: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required("Type is required"),
        text: Yup.string().when("type", {
          is: "text",
          then: () =>
            Yup.string().required(
              t("isNotEmpty", {
                displayName: "text",
              })
            ),
        }),
        image: Yup.string().optional().nullable(),
        tempId: Yup.string().optional().nullable(),
      })
    ),
    model: Yup.string().required(
      t("isNotEmpty", {
        displayName: "questionModel",
      })
    ),
    timeDuration: Yup.string().required(
      t("isNotEmpty", {
        displayName: "timeDuration",
      })
    ),
    marks: Yup.string().required(
      t("isNotEmpty", {
        displayName: "marks",
      })
    ),
    lessonId: Yup.string().required(
      t("isNotEmpty", {
        displayName: "lesson",
      })
    ),
    optionA: Yup.object({
      en: Yup.string(),
      ar: Yup.string().required(
        t("isNotEmpty", {
          displayName: "arabicValue",
        })
      ),
    }),
    optionB: Yup.object({
      en: Yup.string(),
      ar: Yup.string().required(
        t("isNotEmpty", {
          displayName: "arabicValue",
        })
      ),
    }),
    optionC: Yup.object({
      en: Yup.string().nullable(),
      ar: Yup.string().nullable(),
    }),
    optionD: Yup.object({
      en: Yup.string().nullable(),
      ar: Yup.string().nullable(),
    }),
    correctOption: Yup.string().required(
      t("isNotEmpty", {
        displayName: "correctOption",
      })
    ),
  });

  //#region effects

  useEffect(() => {
    if (id) {
      dispatch(getQuestion(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (!gettingQuestion && getQuestionSuccess) {
      if (question?.skills) {
        const skills = question.skills;
        setSkillsSuggestions(
          skills.map((s) => ({ ...s, state: SkillType.CHOSEN_FROM_DROPDOWN }))
        );
        setSelectedSkills(
          skills.map((s) => ({
            value: s.id,
            label: i18n.language === "ar" ? s.name.ar : s.name.en,
          }))
        );
      }
    }
  }, [gettingQuestion]);

  useEffect(() => {
    if (id && question) {
      const contentWithTempIds = question.contents.map((qc) => ({
        ...qc,
        tempId: generateUniqueId(),
      }));
      setImagesProgress(
        contentWithTempIds
          .filter((c) => c.type === "image")
          .map((item: any) => {
            return {
              isError: false,
              isLoading: false,
              isSuccess: true,
              error: null,
              tempId: item.tempId,
              response: {
                image: item.image as string,
                tempId: item.tempId as string,
              },
            };
          })
      );
      formRef.current?.setValues({
        contents: contentWithTempIds,
        lessonId: question.lessonId,
        correctOption: question.correctOption,
        marks: Number(question.marks),
        model: question.model,
        optionA: question.optionA,
        optionB: question.optionB,
        optionC: question.optionC,
        optionD: question.optionD,
        timeDuration: question.timeDuration,
        displayImageId: question.displayImage?.id,
        skills: [],
        question: [],
      });
    }
  }, [question]);

  useEffect(() => {
    if (
      (!addingQuestion && addQuestionSuccess) ||
      (!updatingQuestion && updateQuestionSuccess)
    ) {
      onClose();
    }
  }, [addingQuestion, updatingQuestion]);

  useEffect(() => {
    if (!gettingAISKillsSuggestion && getAISkillsSuggestionSuccess) {
      setSkillsSuggestions([
        ...(aiSuggestedSkills || []),
        ...skillsSuggestions,
      ]);
    }
  }, [gettingAISKillsSuggestion]);

  useEffect(() => {
    if (!questionImageLoading && questionImageSuccess) {
      setImagesProgress(
        imagesProgress.map((ip) => {
          if (ip.tempId == questionImageResponse?.tempId) {
            return {
              ...ip,
              error: null,
              isError: false,
              isLoading: false,
              isSuccess: true,
              response: questionImageResponse as QuestionImageUploadResponse,
            };
          } else {
            return ip;
          }
        })
      );
    }
    if (!questionImageLoading && questionImageError) {
      setImagesProgress(
        imagesProgress.map((ip) => {
          if (ip.tempId == questionImageResponse?.tempId) {
            return {
              ...ip,
              error: questionImageError,
              isError: true,
              isLoading: false,
              isSuccess: false,
              response: null,
            };
          } else {
            return ip;
          }
        })
      );
    }
  }, [questionImageLoading, questionImageSuccess, questionImageError]);

  //#endregion

  //#region handlers
  const onClose = () => {
    formRef.current?.resetForm();
    setSkillsSuggestions([]);
    setSelectedSkills([]);
    setImagesProgress([]);
    onCloseClick(!show);
  };

  const onChangeQuestionImage = async (
    event: ChangeEvent<HTMLInputElement>,
    tempId: string
  ) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      setImagesProgress(
        imagesProgress.map((ip) => {
          if (ip.tempId === tempId) {
            return {
              ...ip,
              error: null,
              isError: false,
              isLoading: true,
              isSuccess: false,
              response: null,
            };
          } else {
            return ip;
          }
        })
      );
      dispatch(uploadQuestionImage(imageFile, history, tempId));
    }
  };

  const onRemoveAISuggestedSkill = (index: number) => {
    skillsSuggestions.splice(index, 1);
    setSelectedSkills(
      skillsSuggestions.map((s: SkillDto) => ({
        value: s.id,
        label: i18n.language === "ar" ? s.name.ar : s.name.en,
      }))
    );
    setSkillsSuggestions([...skillsSuggestions]);
  };

  const onAddNewSkill = () => {
    setSkillsSuggestions([
      {
        id: "",
        content: {
          ar: "",
          en: "",
        },
        name: {
          en: "",
          ar: "",
        },
        createdAt: new Date(),
        createdBy: "",
        deletedAt: new Date(),
        deletedBy: "",
        isActive: true,
        modifiedAt: new Date(),
        modifiedBy: "",
      },
      ...skillsSuggestions,
    ]);
  };

  const onChangeSkills = (changedValue: any) => {
    let _chosenSkills: SkillDto[] | undefined = skillsBySubject
      ?.filter((item: SkillDto) =>
        changedValue.some((filter: any) => filter.value === item.id)
      )
      .map((_skill: SkillDto) => ({
        ..._skill,
        state: SkillType.CHOSEN_FROM_DROPDOWN,
      }));
    setSkillsSuggestions([
      ...(_chosenSkills || []),
      ...skillsSuggestions.filter(
        (sg: SkillDto) => sg.state !== SkillType.CHOSEN_FROM_DROPDOWN
      ),
    ]);
    setSelectedSkills(changedValue);
  };

  const handleFormSubmit = (values: any, { setSubmitting }: any) => {
    //#region custom validations
    if (values.contents.length === 0) {
      toast.error(t("atLeast1ImageOrTextIsRequired"), {
        autoClose: 3000,
      });
      return;
    }
    if (skillsSuggestions.length === 0) {
      toast.error(t("skillRequired"), {
        autoClose: 3000,
      });
      return;
    }
    if (
      imagesProgress.some(
        (ip) => ip.response === null || ip.response.image === null
      )
    ) {
      toast.error(t("uploadImageOrRemoveImageControl"), {
        autoClose: 3000,
      });
      return;
    }
    //#endregion

    values.contents = values.contents.map((item: QuestionContentDto) => {
      if (item.type === "image") {
        const uploadedImagePath = imagesProgress.find(
          (ip) => ip.tempId === item.tempId
        )?.response?.image;
        return {
          ...item,
          image: uploadedImagePath,
        };
      } else {
        return item;
      }
    });

    values.contents = values.contents.map((item: QuestionContentDto) =>
      _.omit(item, "tempId")
    );
    if (id) {
      dispatch(
        updateQuestion(
          id,
          {
            ...values,
            optionA: {
              ...values.optionA,
              en: values.optionA.ar,
            },
            optionB: {
              ...values.optionB,
              en: values.optionB.ar,
            },
            optionC: {
              ...values.optionC,
              en: values.optionC.ar,
            },
            optionD: {
              ...values.optionD,
              en: values.optionD.ar,
            },
            skills: skillsSuggestions,
          },
          history
        )
      );
    } else {
      dispatch(
        addQuestion(
          {
            ...values,
            optionA: {
              ...values.optionA,
              en: values.optionA.ar,
            },
            optionB: {
              ...values.optionB,
              en: values.optionB.ar,
            },
            optionC: {
              ...values.optionC,
              en: values.optionC.ar,
            },
            optionD: {
              ...values.optionD,
              en: values.optionD.ar,
            },
            skills: skillsSuggestions,
          },
          history
        )
      );
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-xl"
        scrollable={true}
      >
        <ModalBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialErrors={addQuestionError || updateQuestionError}
            initialValues={{
              contents: [],
              model: request?.model || model,
              timeDuration: request?.timeDuration || "3:00",
              marks: request?.marks || 1,
              displayImageId: request?.displayImageId,
              lessonId: request?.lessonId || lessonId,
              skills: request?.skills || [],
              optionA: {
                en: request?.optionA.en || "",
                ar: request?.optionA.ar || "",
              },
              optionB: {
                en: request?.optionB.en || "",
                ar: request?.optionB.ar || "",
              },
              optionC: {
                en: request?.optionC.en || "",
                ar: request?.optionC.ar || "",
              },
              optionD: {
                en: request?.optionD.en || "",
                ar: request?.optionD.ar || "",
              },
              correctOption:
                request?.correctOption || QuestionOptionEnum.OPTION_A,
            }}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              setFieldValue,
              touched,
              setFieldError,
              errors,
              setFieldTouched,
              handleBlur,
              handleChange,
            }) => (
              <Form id="form1">
                <FieldArray name="contents">
                  {({ remove, push }) => (
                    <>
                      {values.contents.length > 0 &&
                        values.contents.map(
                          (
                            field: {
                              type: string;
                              text?: string;
                              image?: string;
                              tempId?: string;
                            },
                            index: number
                          ) => (
                            <div key={index}>
                              {field.type === "text" && (
                                <Row className="p-2 mb-2">
                                  <Col xl="12">
                                    <div className="mb-3">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Label
                                          htmlFor={`contents.${index}.text`}
                                          className="form-label fs-5"
                                        >
                                          {t("questionText")}
                                          <span className="text-danger">*</span>
                                        </Label>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => remove(index)}
                                        >
                                          {t("removeQuestionText")}
                                        </button>
                                      </div>
                                      <AppRichEditor
                                        initialValue={
                                          question?.contents[index]?.text || ""
                                        }
                                        onChange={(data: any) => {
                                          setFieldValue(
                                            `contents.${index}.text`,
                                            data
                                          );
                                        }}
                                        onKeyUp={(data: any) => {
                                          if (data.trim().length == 0) {
                                            setFieldError(
                                              `contents.${index}.text`,
                                              t("isNotEmpty", {
                                                displayName: "arabicValue",
                                              })
                                            );
                                          } else {
                                            setFieldError(
                                              `contents.${index}.text`,
                                              ""
                                            );
                                          }
                                        }}
                                        onFocus={() => {
                                          setFieldTouched(
                                            `contents.${index}.text`,
                                            true,
                                            true
                                          );
                                        }}
                                      />
                                      {errors &&
                                      errors.contents &&
                                      errors.contents.length &&
                                      errors.contents.length > 0 ? (
                                        <Label className="invalid-feedback d-block">
                                          {
                                            (errors.contents[index] as any)
                                              ?.text
                                          }
                                        </Label>
                                      ) : null}
                                    </div>
                                  </Col>
                                </Row>
                              )}
                              {field.type === "image" && (
                                <Row className="p-2 mb-2">
                                  <Col sm={12} className="text-center">
                                    <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                                      <img
                                        id={`image_${field.tempId}`}
                                        src={
                                          imagesProgress.find(
                                            (i) => i.tempId === field.tempId
                                          )?.response?.image ||
                                          question?.contents[index]?.image ||
                                          profile
                                        }
                                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                                        style={{
                                          height: "10.5rem",
                                          width: "10.5rem",
                                        }}
                                        alt="user-profile"
                                      />
                                      <div
                                        className="avatar-xl p-0 rounded-circle profile-photo-edit"
                                        style={{
                                          right: "2px",
                                          bottom: "8px",
                                        }}
                                      >
                                        <Input
                                          id={`question_image_${field.tempId}`}
                                          type="file"
                                          className="profile-img-file-input"
                                          accept={IMAGE_ACCEPT}
                                          onChange={(
                                            event: ChangeEvent<HTMLInputElement>
                                          ) =>
                                            onChangeQuestionImage(
                                              event,
                                              field.tempId as string
                                            )
                                          }
                                          disabled={
                                            imagesProgress.find(
                                              (i) => i.tempId === field.tempId
                                            )?.isLoading
                                          }
                                        />
                                        <Label
                                          htmlFor={`question_image_${field.tempId}`}
                                          className="profile-photo-edit avatar-xs"
                                        >
                                          <span className="avatar-title rounded-circle bg-light text-body">
                                            {imagesProgress.find(
                                              (i) => i.tempId === field.tempId
                                            )?.isLoading && <Spinner />}
                                            {!imagesProgress.find(
                                              (i) => i.tempId === field.tempId
                                            )?.isLoading && (
                                              <i className="ri-camera-fill"></i>
                                            )}
                                          </span>
                                        </Label>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col sm={12} className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => {
                                        imagesProgress.splice(
                                          imagesProgress.findIndex(
                                            (i) => i.tempId === field.tempId
                                          ),
                                          1
                                        );
                                        setImagesProgress([...imagesProgress]);
                                        remove(index);
                                      }}
                                    >
                                      {t("removeQuestionImage")}
                                    </button>
                                  </Col>
                                </Row>
                              )}
                            </div>
                          )
                        )}
                      {values.contents.length === 0 && (
                        <div className="d-flex align-items-center">
                          <i className="ri-information-line text-info pe-1"></i>
                          <span className="text-info">
                            {t("atLeast1ImageOrTextIsRequired")}
                          </span>
                        </div>
                      )}
                      <div className="d-flex py-3 justify-content-end align-items-center">
                        <button
                          type="button"
                          className="btn btn-primary mx-1"
                          onClick={() => {
                            const newId = generateUniqueId();
                            push({ type: "text", text: null, id: newId });
                          }}
                        >
                          {t("addText")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            const newId = generateUniqueId();
                            setImagesProgress([
                              ...imagesProgress,
                              {
                                error: null,
                                isError: false,
                                isLoading: false,
                                isSuccess: false,
                                response: null,
                                tempId: newId,
                              },
                            ]);
                            push({
                              type: "image",
                              image: null,
                              tempId: newId,
                            });
                          }}
                        >
                          {t("addImage")}
                        </button>
                      </div>
                    </>
                  )}
                </FieldArray>
                <>
                  <Row>
                    <Col xl="6">
                      <Label htmlFor="optionA.ar" className="form-label">
                        {t("answerA")}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={question?.optionA?.ar || ""}
                          onChange={(data: any) => {
                            setFieldValue("optionA.ar", data);
                          }}
                          onKeyUp={(data: any) => {
                            if (data.trim().length == 0) {
                              setFieldError(
                                "optionA.ar",
                                t("isNotEmpty", {
                                  displayName: "arabicValue",
                                })
                              );
                            } else {
                              setFieldError("optionA.ar", "");
                            }
                          }}
                          onFocus={() => {
                            setFieldTouched("optionA.ar", true, true);
                          }}
                        />
                        {touched.optionA?.ar && errors.optionA?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionA?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                    <Col xl="6">
                      <Label htmlFor="optionB.ar" className="form-label">
                        {t("answerB")}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={question?.optionB?.ar || ""}
                          onChange={(data: any) => {
                            setFieldValue("optionB.ar", data);
                          }}
                          onKeyUp={(data: any) => {
                            if (data.trim().length == 0) {
                              setFieldError(
                                "optionB.ar",
                                t("isNotEmpty", {
                                  displayName: "arabicValue",
                                })
                              );
                            } else {
                              setFieldError("optionB.ar", "");
                            }
                          }}
                          onFocus={() => {
                            setFieldTouched("optionB.ar", true, true);
                          }}
                        />
                        {touched.optionB?.ar && errors.optionB?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionB?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="6">
                      <Label htmlFor="optionC.ar" className="form-label">
                        {t("answerC")}
                      </Label>
                      <div className="mb-3">
                        <AppRichEditor
                          initialValue={question?.optionC?.ar || ""}
                          onChange={(data: any) => {
                            setFieldValue("optionC.ar", data);
                          }}
                          onFocus={() => {
                            setFieldTouched("optionC.ar", true, true);
                          }}
                        />
                        {touched.optionC?.ar && errors.optionC?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionC?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                    <Col xl="6">
                      <div className="mb-3">
                        <Label htmlFor="optionD.ar" className="form-label">
                          {t("answerD")}
                        </Label>
                        <AppRichEditor
                          initialValue={question?.optionD?.ar || ""}
                          onChange={(data: any) => {
                            setFieldValue("optionD.ar", data);
                          }}
                          onFocus={() => {
                            setFieldTouched("optionD.ar", true, true);
                          }}
                        />
                        {touched.optionD?.ar && errors.optionD?.ar ? (
                          <Label className="invalid-feedback d-block">
                            {errors.optionD?.ar as string}
                          </Label>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="12">
                      <Row>
                        <Col sm={4}>
                          <div className="mb-3">
                            <Label
                              htmlFor="correctOption"
                              className="form-label"
                            >
                              {t("correctAnswer")}
                            </Label>
                            <Input
                              type="select"
                              id="correctOption"
                              name="correctOption"
                              className="form-select"
                              aria-label={t("chooseCorrectAnswer")}
                              placeholder={t("chooseCorrectAnswer")}
                              value={values.correctOption}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value={""}>
                                {t("chooseCorrectAnswer")}{" "}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_A}
                                key={"A"}
                                className="text-capitalize"
                              >
                                {t("A")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_B}
                                key={"B"}
                                className="text-capitalize"
                              >
                                {t("B")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_C}
                                key={"C"}
                                className="text-capitalize"
                              >
                                {t("C")}
                              </option>
                              <option
                                value={QuestionOptionEnum.OPTION_D}
                                key={"D"}
                                className="text-capitalize"
                              >
                                {t("D")}
                              </option>
                            </Input>
                            {touched.correctOption && errors.correctOption ? (
                              <FormFeedback type="invalid">
                                {errors.correctOption as string}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col sm={4}>
                          <Label htmlFor="marks" className="form-label">
                            {t("marks")}
                          </Label>
                          <div className="mb-3">
                            <div className="input-step w-100">
                              <button
                                type="button"
                                className="minus"
                                onClick={() => {
                                  if (values.marks > 1)
                                    setFieldValue("marks", values.marks + -1);
                                }}
                              >
                                –
                              </button>
                              <Input
                                type="text"
                                className="product-quantity w-100"
                                value={values.marks}
                                name="marks"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                readOnly
                              />
                              <button
                                type="button"
                                className="plus"
                                onClick={() => {
                                  setFieldValue("marks", values.marks + 1);
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <Label htmlFor="time" className="form-label">
                            {t("time")}
                          </Label>
                          <div className="mb-3">
                            <TimeInput
                              id="time"
                              name="time"
                              value={values.timeDuration}
                              className="form-control"
                              onChangeValue={(value: string) => {
                                setFieldValue("timeDuration", value);
                              }}
                            />
                            {touched.timeDuration && errors.timeDuration ? (
                              <FormFeedback type="invalid">
                                {errors.timeDuration as string}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="mb-3">
                            <Label htmlFor="skillId" className="form-label">
                              {t("chooseExistingSkills")}
                            </Label>
                            <SkillsSelect
                              id="skillId"
                              subjectId={subjectId}
                              placeholder="chooseSkills"
                              isMulti={true}
                              closeMenuOnSelect={false}
                              value={selectedSkills}
                              onChange={onChangeSkills}
                            ></SkillsSelect>
                          </div>
                        </Col>
                      </Row>
                      <Card>
                        <CardHeader>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <h5 className="card-title mb-0">
                                {t("aiReadSkills")}
                              </h5>
                            </div>
                            <div
                              title={t("addSkillManually")}
                              className="avatar-xs d-block flex-shrink-0 ms-3 cursor-pointer"
                              onClick={onAddNewSkill}
                            >
                              <span className="avatar-title rounded-circle fs-16 bg-success text-light">
                                <i className=" ri-add-line"></i>
                              </span>
                            </div>
                          </div>
                        </CardHeader>
                        {skillsSuggestions && (
                          <CardBody>
                            {skillsSuggestions.map(
                              (skill: SkillDto, key: number) => (
                                <React.Fragment key={key}>
                                  <div className="mb-3 d-flex">
                                    <Input
                                      required
                                      id={key + "ai_suggested_skill_name"}
                                      name={key + "ai_suggested_skill_name"}
                                      type="text"
                                      value={skill.name.ar}
                                      className="form-control"
                                      placeholder={skill.name.ar}
                                      onChange={(
                                        event: ChangeEvent<HTMLInputElement>
                                      ) => {
                                        skillsSuggestions[key].name.ar =
                                          event.target.value;
                                        skillsSuggestions[key].name.en =
                                          event.target.value;
                                        setSkillsSuggestions([
                                          ...skillsSuggestions,
                                        ]);
                                        if (skillsSuggestions[key].id) {
                                          setSelectedSkills(
                                            skillsSuggestions.map((s) => ({
                                              value: s.id,
                                              label:
                                                i18n.language === "ar"
                                                  ? s.name.ar
                                                  : s.name.en,
                                            }))
                                          );
                                        }
                                      }}
                                    />
                                    <div
                                      className="avatar-xs d-block flex-shrink-0 ms-3 cursor-pointer"
                                      onClick={() =>
                                        onRemoveAISuggestedSkill(key)
                                      }
                                    >
                                      <span
                                        className="avatar-title rounded-circle fs-16 bg-danger text-light"
                                        title={t("deleteSkillFromQuestion")}
                                      >
                                        <i className=" ri-delete-bin-line"></i>
                                      </span>
                                    </div>
                                  </div>
                                  <div className="mb-3 d-flex">
                                    <Input
                                      required
                                      id={
                                        key + "ai_suggested_skill_description"
                                      }
                                      name={
                                        key + "ai_suggested_skill_description"
                                      }
                                      rows={5}
                                      value={skill.content.ar}
                                      type="textarea"
                                      className="form-control"
                                      placeholder={skill.content.ar}
                                      onChange={(
                                        event: ChangeEvent<HTMLInputElement>
                                      ) => {
                                        skillsSuggestions[key].content.ar =
                                          event.target.value;
                                        skillsSuggestions[key].content.en =
                                          event.target.value;
                                        setSkillsSuggestions([
                                          ...skillsSuggestions,
                                        ]);
                                        if (skillsSuggestions[key].id) {
                                          setSelectedSkills(
                                            skillsSuggestions.map((s) => ({
                                              value: s.id,
                                              label:
                                                i18n.language === "ar"
                                                  ? s.name.ar
                                                  : s.name.en,
                                            }))
                                          );
                                        }
                                      }}
                                    />
                                    <div className="avatar-xs d-block flex-shrink-0 ms-3 invisible"></div>
                                  </div>
                                </React.Fragment>
                              )
                            )}
                          </CardBody>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </>
              </Form>
            )}
          </Formik>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            form="form1"
            inProgress={addingQuestion || updatingQuestion}
            disabled={
              gettingQuestion ||
              addingQuestion ||
              updatingQuestion ||
              getQuestionError ||
              questionImageLoading ||
              gettingAISKillsSuggestion ||
              imagesProgress.some((ip) => ip.isLoading)
            }
          ></ActivityButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};
